import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Wallet, Check } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { PageHeader } from '@/components/ui/PageHeader'
import { Card } from '@/components/ui/Card'
import { CenterSpinner } from '@/components/ui/Spinner'
import { EmptyState } from '@/components/ui/EmptyState'
import { useAuth } from '@/lib/auth/AuthProvider'
import { useToast } from '@/components/ui/ToastProvider'
import { dateShort, money } from '@/lib/format'

interface ReimburseInvoice {
  id: string
  invoiceNumber: string | null
  supplierName: string | null
  issueDate: string | null
  gross: number | null
  currency: string
  paymentStatus: string
}

interface ReimburseUser {
  userId: string
  userName: string | null
  userEmail: string | null
  invoices: ReimburseInvoice[]
  totalToRefund: number
  totalRefunded: number
}

function token() {
  return localStorage.getItem('adaptivo.szamla.token')
}

export function ReimbursementsPage() {
  const { activeCompanyId } = useAuth()
  const toast = useToast()
  const qc = useQueryClient()
  const [selected, setSelected] = useState<Record<string, Set<string>>>({}) // userId → invoice IDs

  const q = useQuery({
    queryKey: ['reimbursements', activeCompanyId],
    queryFn: async () => {
      const t = token()
      const r = await fetch(`/api/v1/reimbursements?companyId=${activeCompanyId}`, {
        headers: t ? { Authorization: `Bearer ${t}` } : {},
      })
      if (!r.ok) throw new Error(`Reimbursements: ${r.status}`)
      return (await r.json()) as { byUser: ReimburseUser[]; unassigned: { count: number; gross: number } }
    },
    enabled: !!activeCompanyId,
  })

  const markPaid = useMutation({
    mutationFn: async ({ userId, invoiceIds }: { userId: string; invoiceIds: string[] }) => {
      const t = token()
      const r = await fetch(`/api/v1/reimbursements/mark-paid`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(t ? { Authorization: `Bearer ${t}` } : {}) },
        body: JSON.stringify({ userId, invoiceIds }),
      })
      if (!r.ok) throw new Error(`Mark paid: ${r.status}`)
      return r.json() as Promise<{ ok: boolean; updated: number }>
    },
    onSuccess: (data, vars) => {
      toast.show({ tone: 'success', title: `${data.updated} számla "kifizetve" — visszatérítve` })
      setSelected((p) => ({ ...p, [vars.userId]: new Set() }))
      qc.invalidateQueries({ queryKey: ['reimbursements'] })
      qc.invalidateQueries({ queryKey: ['invoices'] })
    },
    onError: (err) => {
      toast.show({ tone: 'error', title: 'Hiba', description: err instanceof Error ? err.message : String(err) })
    },
  })

  if (!activeCompanyId) return <p className="text-sm text-slate-500">Nincs aktív cég.</p>

  function toggle(userId: string, invoiceId: string) {
    setSelected((p) => {
      const set = new Set(p[userId] ?? [])
      if (set.has(invoiceId)) set.delete(invoiceId)
      else set.add(invoiceId)
      return { ...p, [userId]: set }
    })
  }

  function selectAll(userId: string, invoiceIds: string[]) {
    setSelected((p) => ({ ...p, [userId]: new Set(invoiceIds) }))
  }

  return (
    <div>
      <PageHeader
        accent
        eyebrow="Pénzügyi modul"
        title="Saját kártya elszámolások"
        subtitle="Munkatársak által saját kártyával kifizetett számlák visszatérítése. Jelöld be amit átutaltál, és kattints a Visszatérítve gombra."
      />

      {q.isLoading ? (
        <CenterSpinner />
      ) : !q.data || q.data.byUser.length === 0 ? (
        <EmptyState
          variant="success"
          title="Nincs visszatérítendő"
          description="Senki nem fizetett ki saját kártyával számlát — vagy mind már elszámolva."
        />
      ) : (
        <div className="space-y-3">
          {q.data.byUser.map((u) => {
            const pending = u.invoices.filter((i) => i.paymentStatus !== 'paid')
            const sel = selected[u.userId] ?? new Set<string>()
            const selTotal = pending.filter((i) => sel.has(i.id)).reduce((s, i) => s + (i.gross ?? 0), 0)
            return (
              <Card key={u.userId} padding="none" className="overflow-hidden">
                <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between gap-3 bg-slate-50/60 dark:bg-slate-900/40">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <div className="grid h-8 w-8 place-items-center rounded-md bg-brand-100 dark:bg-brand-900/40 text-brand-700 dark:text-brand-200">
                      <Wallet className="h-4 w-4" />
                    </div>
                    <div className="min-w-0">
                      <div className="font-semibold truncate">{u.userName ?? '(név nélkül)'}</div>
                      <div className="text-xs text-slate-500 dark:text-slate-400 truncate">{u.userEmail ?? ''}</div>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-xs text-slate-500">Visszatérítendő</div>
                    <div className="text-lg font-semibold num-display text-amber-700 dark:text-amber-300">
                      {money(u.totalToRefund, 'HUF')}
                    </div>
                    {u.totalRefunded > 0 && (
                      <div className="text-[11px] text-slate-500">Eddig térítve: {money(u.totalRefunded, 'HUF')}</div>
                    )}
                  </div>
                </div>

                <table className="w-full text-sm">
                  <thead className="bg-white dark:bg-slate-900 text-slate-500 text-xs">
                    <tr>
                      <th className="px-3 py-2 w-8">
                        <input
                          type="checkbox"
                          checked={pending.length > 0 && pending.every((i) => sel.has(i.id))}
                          onChange={(e) => (e.target.checked ? selectAll(u.userId, pending.map((i) => i.id)) : selectAll(u.userId, []))}
                        />
                      </th>
                      <th className="text-left px-3 py-2">Számla</th>
                      <th className="text-left px-3 py-2">Szállító</th>
                      <th className="text-left px-3 py-2">Kelt</th>
                      <th className="text-right px-3 py-2">Bruttó</th>
                      <th className="text-left px-3 py-2">Státusz</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                    {u.invoices.map((i) => {
                      const paid = i.paymentStatus === 'paid'
                      return (
                        <tr key={i.id} className={paid ? 'opacity-60' : 'hover:bg-slate-50 dark:hover:bg-slate-900/50'}>
                          <td className="px-3 py-2">
                            {!paid && (
                              <input
                                type="checkbox"
                                checked={sel.has(i.id)}
                                onChange={() => toggle(u.userId, i.id)}
                              />
                            )}
                          </td>
                          <td className="px-3 py-2 font-mono text-xs">{i.invoiceNumber ?? '—'}</td>
                          <td className="px-3 py-2">{i.supplierName ?? '—'}</td>
                          <td className="px-3 py-2 text-xs text-slate-500">{dateShort(i.issueDate)}</td>
                          <td className="px-3 py-2 text-right num-display">{money(i.gross, i.currency)}</td>
                          <td className="px-3 py-2 text-xs">
                            {paid ? (
                              <span className="inline-flex items-center gap-1 text-emerald-700 dark:text-emerald-300">
                                <Check className="h-3 w-3" /> Visszatérítve
                              </span>
                            ) : (
                              <span className="text-amber-700 dark:text-amber-300">Vár</span>
                            )}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>

                {sel.size > 0 && (
                  <div className="px-4 py-2 border-t border-slate-100 dark:border-slate-800 bg-brand-50/50 dark:bg-brand-950/20 flex items-center justify-between gap-3">
                    <div className="text-sm">
                      <strong>{sel.size}</strong> számla kiválasztva · összesen{' '}
                      <strong className="num-display">{money(selTotal, 'HUF')}</strong>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => markPaid.mutate({ userId: u.userId, invoiceIds: Array.from(sel) })}
                      loading={markPaid.isPending}
                      leftIcon={<Check className="h-3.5 w-3.5" />}
                    >
                      Visszatérítve — átutaltam
                    </Button>
                  </div>
                )}
              </Card>
            )
          })}

          {q.data.unassigned.count > 0 && (
            <div className="rounded-md border border-amber-200 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-900/50 px-3 py-2 text-xs text-amber-800 dark:text-amber-200">
              <strong>{q.data.unassigned.count}</strong> saját-kártyás számla, amelynél a "Ki fizette" user nincs beállítva
              · {money(q.data.unassigned.gross, 'HUF')}. A számla részleteinél állítsd be.
            </div>
          )}
        </div>
      )}
    </div>
  )
}
